import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import type { FetchQueueResponse } from '../../types';

const REFRESH_MS = 10_000;

/** What the fetch queue holds right now: waiting, in flight per adapter, and the latest
 *  failures with every adapter's reason. Refreshes itself. */
export default function FetchQueuePanel({ labels }: { labels: Record<string, string> }) {
  const [data, setData] = useState<FetchQueueResponse | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      setData(await api.getFetchQueue());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the queue');
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!data) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>;
  const inFlight = Object.entries(data.in_flight);
  return (
    <div className="space-y-3 text-sm">
      <p className="text-gray-700 dark:text-gray-200">
        <strong>{data.waiting_total}</strong> waiting
        {data.oldest_waiting_s != null && data.waiting_total > 0 && (
          <span className="text-gray-500 dark:text-gray-400"> · oldest {formatS(data.oldest_waiting_s)}</span>
        )}
        {' · '}
        {inFlight.length === 0
          ? 'nothing in flight'
          : inFlight.map(([a, n]) => `${n} on ${labels[a] ?? a}`).join(', ')}
      </p>
      {data.waiting_total > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {Object.entries(data.waiting)
            .map(([t, byOrigin]) => `${t}: ${Object.values(byOrigin).reduce((a, b) => a + b, 0)}`)
            .join(' · ')}
        </p>
      )}
      {data.failures.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs text-gray-600 dark:text-gray-300">
            {data.failures.length} failed in the last day
          </summary>
          <ul className="mt-2 space-y-1 max-h-64 overflow-y-auto">
            {data.failures.map((f) => (
              <li key={f.id} className="text-xs">
                <span className="text-gray-800 dark:text-gray-100">{f.url || f.query}</span>
                <span className="text-gray-400"> · {f.type}{f.origin ? ` · ${f.origin}` : ''}</span>
                <div className="text-gray-500 dark:text-gray-400">{f.error}</div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function formatS(s: number): string {
  if (s < 90) return `${s}s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  return `${Math.round(s / 3600)} h`;
}
